import { useEffect, useState } from "react";
import axios from "axios";
import { api, API_BASE } from "@/lib/api";
import { X, FilePdf, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ClientCombobox } from "@/components/ClientCombobox";
import { HolidayDatesEditor } from "@/components/HolidayDatesEditor";
import { useAuth } from "@/context/AuthContext";

const empty = {
  company_name: "", location: "", description: "",
  contact_name: "", contact_email: "", contact_phone: "",
  hourly_rate: 0,
  minimum_hours_applies: false, minimum_hours_per_week: "",
  per_diem_applies: false, per_diem_rate: 0, per_diem_frequency: "every_day_worked",
  ot_rate_applies: false, ot_rate: 0, ot_threshold_hours: 40,
  holiday_rate_applies: false, holiday_rate: 0, holiday_dates: [],
  mobilization_applies: false, mobilization_hours_cap: "",
  special_terms: "",
  anticipated_start_date: "TBD", anticipated_duration: "TBD",
  validity_days: 30,
  cc_emails: [],
  auto_convert: true, project_name: "", po_number: "", client_initials: "",
};

const fromInitial = (i) => i ? {
  company_name: i.company_name || "",
  location: i.location || "",
  description: i.description || "",
  contact_name: i.contact_name || "",
  contact_email: i.contact_email || "",
  contact_phone: i.contact_phone || "",
  hourly_rate: i.hourly_rate ?? 0,
  minimum_hours_applies: !!i.minimum_hours_applies,
  minimum_hours_per_week: i.minimum_hours_per_week ?? "",
  per_diem_applies: !!i.per_diem_applies,
  per_diem_rate: i.per_diem_rate ?? 0,
  per_diem_frequency: i.per_diem_frequency || "every_day_worked",
  ot_rate_applies: !!i.ot_rate_applies,
  ot_rate: i.ot_rate ?? 0,
  ot_threshold_hours: i.ot_threshold_hours ?? 40,
  holiday_rate_applies: !!i.holiday_rate_applies,
  holiday_rate: i.holiday_rate ?? 0,
  holiday_dates: i.holiday_dates || [],
  mobilization_applies: !!i.mobilization_applies,
  mobilization_hours_cap: i.mobilization_hours_cap ?? "",
  special_terms: i.special_terms || "",
  anticipated_start_date: i.anticipated_start_date || "TBD",
  anticipated_duration: i.anticipated_duration || "TBD",
  validity_days: i.validity_days ?? 30,
  cc_emails: i.cc_emails || [],
  auto_convert: i.auto_convert !== false,
  project_name: i.auto_convert_prefs?.project_name || "",
  po_number: i.auto_convert_prefs?.po_number || "",
  client_initials: i.auto_convert_prefs?.client_initials || "",
} : empty;

const NEW_CLIENT = "__new__";

// mode="sales": a sales rep drafts the proposal → goes to /sales/proposals. With autoSend (admin turned off review for this rep)
// it's emailed to the client immediately; otherwise it waits for admin review.
export default function NewProposalModal({ onClose, onCreated, initial, mode = "admin", autoSend = false }) {
  const { user } = useAuth();
  const isSales = mode === "sales";
  const needsReview = isSales && !autoSend;
  const isAdmin = !isSales && user?.role === "admin";
  const isEdit = !!initial;
  const [f, setF] = useState(fromInitial(initial));
  const [clients, setClients] = useState([]);
  const [reps, setReps] = useState([]);
  const [repId, setRepId] = useState(initial?.sales_rep_id || "");
  // "" = not chosen yet, "__new__" = brand-new client (manual entry), else = existing client id
  const [clientId, setClientId] = useState("");
  const [resendOnSave, setResendOnSave] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ccInput, setCcInput] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/sales-reps").then((r) => setReps(Array.isArray(r.data) ? r.data : [])).catch(() => setReps([]));
  }, [isAdmin]);

  const addCc = () => {
    const e = ccInput.trim();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Enter a valid email address."); return; }
    if (f.cc_emails.some((x) => x.toLowerCase() === e.toLowerCase())
        || e.toLowerCase() === (f.contact_email || "").toLowerCase()) {
      toast.error("That address is already included."); return;
    }
    set("cc_emails", [...f.cc_emails, e]);
    setCcInput("");
  };
  const removeCc = (e) => set("cc_emails", f.cc_emails.filter((x) => x !== e));

  // Only load the client list on the create flow — editing a proposal shouldn't
  // re-parent it to a different client.
  useEffect(() => {
    if (isEdit) return;
    api.get(isSales ? "/sales/clients" : "/clients").then((r) => setClients(Array.isArray(r.data) ? r.data : []))
       .catch(() => setClients([]));
  }, [isEdit, isSales]);

  const pickClient = (cid) => {
    if (!cid || cid === NEW_CLIENT) {
      setClientId(NEW_CLIENT);
      // Wipe the fields so the admin knows they're entering a brand-new client
      setF((p) => ({ ...p, company_name: "", contact_name: "", contact_email: "", contact_phone: "" }));
      return;
    }
    const c = clients.find((x) => x.id === cid);
    if (!c) return;
    setClientId(cid);
    setF((p) => ({
      ...p,
      company_name: c.name || "",
      contact_name: c.contact_name || "",
      contact_email: c.billing_email || c.contact_email || "",
      contact_phone: c.contact_phone || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        hourly_rate: Number(f.hourly_rate) || 0,
        per_diem_rate: Number(f.per_diem_rate) || 0,
        ot_rate: f.ot_rate_applies ? (Number(f.ot_rate) || 0) : 0,
        ot_threshold_hours: f.ot_rate_applies ? (Number(f.ot_threshold_hours) || 40) : 40,
        holiday_rate: f.holiday_rate_applies ? (Number(f.holiday_rate) || 0) : 0,
        holiday_dates: f.holiday_rate_applies ? (f.holiday_dates || []) : [],
        minimum_hours_per_week: f.minimum_hours_applies && f.minimum_hours_per_week
          ? Number(f.minimum_hours_per_week) : null,
        mobilization_hours_cap: f.mobilization_applies && f.mobilization_hours_cap
          ? Number(f.mobilization_hours_cap) : null,
        validity_days: Number(f.validity_days) || 30,
        ...(isAdmin ? { sales_rep_id: repId || null } : {}),
        ...(isEdit && !isSales ? { resend_after_save: resendOnSave } : {}),
      };
      let r;
      if (isSales) {
        r = isEdit
          ? await axios.put(`${API_BASE}/sales/proposals/${initial.id}`, payload, { withCredentials: true })
          : await axios.post(`${API_BASE}/sales/proposals`, payload, { withCredentials: true });
        if (r.data?.status === "sent") {
          if (r.data.sent === false) toast.error("Proposal saved, but the email to the client could not be delivered — the office has been alerted and can resend.", { duration: 9000 });
          else toast.success(`Proposal sent to ${f.contact_email}. The office has been notified.`);
        }
        else toast.success(isEdit ? "Changes submitted — an admin will review before it goes to the client." : "Proposal submitted for admin review.");
      } else if (isEdit) {
        r = await axios.put(`${API_BASE}/proposals/${initial.id}`, payload, { withCredentials: true });
        toast.success(resendOnSave
          ? (r.data?.sent ? "Proposal updated and resent." : "Proposal updated (email failed — try Resend).")
          : "Proposal updated (email not resent).");
      } else {
        r = await axios.post(`${API_BASE}/proposals`, payload, { withCredentials: true });
        toast.success(r.data?.sent ? "Proposal sent to the client." : "Proposal created (email failed — try Resend).");
      }
      onCreated?.(r.data);
      onClose();
    } catch (er) {
      toast.error(er.response?.data?.detail?.toString() || er.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 overflow-y-auto" data-testid="new-proposal-modal">
      <div className="flex items-start sm:items-center justify-center min-h-full p-4">
      <form onSubmit={submit} className="bg-white rounded-md w-full max-w-2xl my-4 sm:my-8 p-6 border border-slate-200 shadow-lg">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="tinylabel text-teal-700">{needsReview ? "Draft for admin review" : isSales ? "Goes straight to the client" : isEdit ? "Update commercial terms" : "Recruit & sell"}</div>
            <h2 className="text-2xl font-black">{isEdit ? (isSales ? "Revise Proposal" : "Edit Proposal") : "New Proposal"}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={22} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {needsReview
            ? "Fill in the client and terms. An admin reviews it and sends it to the client — you'll get an email when it's approved or sent back with notes."
            : isSales
            ? "Fill in the client and terms. When you click Send, the client gets the branded email + PDF with their acceptance link right away, and the office is notified."
            : isEdit
            ? (["pending_review", "changes_requested"].includes(initial?.status)
                ? "This draft came from a sales rep and hasn't been sent. Adjust any terms; with \"Approve & send\" checked it goes straight to the client when you save."
                : "Update any terms below. If \"Resend to client\" is checked, a fresh 30-day link + PDF will be emailed.")
            : "Send a stylized proposal + signed-PDF link to a prospective client. When they accept, the Client + Project can be created automatically."}
        </p>
        {isSales && isEdit && initial?.review_note && (
          <div className="mb-4 rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-900" data-testid="prop-review-note">
            <b>Admin note:</b> “{initial.review_note}”
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 -mb-1">
            <div className="tinylabel text-slate-600">Prospective client</div>
          </div>
          {!isEdit && (
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Existing client or new</label>
              <ClientCombobox
                value={clientId}
                onChange={pickClient}
                clients={clients}
                withNewOption
                testId="prop-client-picker"
                placeholder="Choose client…"
              />
              {clientId && clientId !== NEW_CLIENT && (
                <div className="mt-1 text-xs text-teal-800 bg-teal-50 border border-teal-200 rounded-md px-2.5 py-1.5">
                  Pre-filled from the client record. Anything below can still be edited for this specific proposal.
                </div>
              )}
              {clientId === NEW_CLIENT && (
                <div className="mt-1 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="new-client-hint">
                  <b>New client.</b> Enter the <b>company name</b>, <b>contact info</b>, and terms in the fields below — the picker above just marks this proposal as being for a brand-new client.
                </div>
              )}
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client / Company name</label>
            <input required value={f.company_name} onChange={(e) => set("company_name", e.target.value)}
              data-testid="prop-company_name" className={`mt-1 w-full h-11 px-3 border border-slate-300 rounded-md ${clientId && clientId !== NEW_CLIENT ? "bg-slate-50" : ""}`} placeholder="e.g. Turner Construction" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Contact name</label>
            <input required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)}
              data-testid="prop-contact_name" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Contact email</label>
            <input required type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)}
              data-testid="prop-contact_email" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Contact phone</label>
            <input required value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)}
              data-testid="prop-contact_phone" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Send copies to (CC)</label>
            <div className="mt-1 flex gap-2">
              <input type="email" value={ccInput} onChange={(e) => setCcInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                placeholder="name@company.com — press Enter or Add"
                data-testid="prop-cc-input" className="flex-1 h-11 px-3 border border-slate-300 rounded-md" />
              <button type="button" onClick={addCc} data-testid="prop-cc-add-btn"
                className="h-11 px-4 border border-green-600 text-green-700 font-semibold rounded-md hover:bg-green-50">Add</button>
            </div>
            {f.cc_emails.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="prop-cc-list">
                {f.cc_emails.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-3 py-1 text-sm text-slate-700" data-testid={`prop-cc-chip-${e}`}>
                    {e}
                    <button type="button" onClick={() => removeCc(e)} data-testid={`prop-cc-remove-${e}`}
                      className="text-slate-400 hover:text-red-600 font-bold leading-none">×</button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-1 text-xs text-slate-400">Copies get the proposal PDF for their records — only the contact above receives the acceptance link.</div>
          </div>

          <div className="sm:col-span-2 mt-4 mb-1 pt-4 border-t border-slate-200">
            <div className="tinylabel text-slate-600">Project</div>
          </div>
          {isAdmin && reps.length > 0 && (
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Sales rep credited</label>
              <select value={repId} onChange={(e) => setRepId(e.target.value)} data-testid="prop-sales-rep"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                <option value="">— None (owner-sold) —</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <div className="mt-1 text-xs text-slate-500">Carried onto the project when the client accepts — approved hours on it count toward the rep's pay.</div>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project location</label>
            <input required value={f.location} onChange={(e) => set("location", e.target.value)}
              data-testid="prop-location" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="e.g. Columbus, OH — 123 Main St site" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project description</label>
            <textarea required rows={3} value={f.description} onChange={(e) => set("description", e.target.value)}
              data-testid="prop-description" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" placeholder="Brief description of the work / role" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anticipated start</label>
            <input value={f.anticipated_start_date} onChange={(e) => set("anticipated_start_date", e.target.value)}
              data-testid="prop-anticipated_start_date" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="TBD or a specific date" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Anticipated duration</label>
            <input value={f.anticipated_duration} onChange={(e) => set("anticipated_duration", e.target.value)}
              data-testid="prop-anticipated_duration" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" placeholder="TBD, ~6 weeks, etc." />
          </div>

          <div className="sm:col-span-2 mt-4 mb-1 pt-4 border-t border-slate-200">
            <div className="tinylabel text-slate-600">Commercial terms</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Hourly rate ($ / hr)</label>
            <input required type="number" step="0.01" value={f.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)}
              data-testid="prop-hourly_rate" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
            <div className="mt-1 text-xs text-slate-500">Straight time only.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Minimum hours / week?</label>
            <div className="mt-1 flex items-center gap-2 h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.minimum_hours_applies}
                  onChange={(e) => set("minimum_hours_applies", e.target.checked)}
                  data-testid="prop-minimum_hours_applies" className="h-4 w-4 accent-teal-600" />
                Guarantee
              </label>
              {f.minimum_hours_applies && (
                <input type="number" step="1" value={f.minimum_hours_per_week}
                  onChange={(e) => set("minimum_hours_per_week", e.target.value)}
                  data-testid="prop-minimum_hours_per_week" placeholder="hrs / week"
                  className="flex-1 h-9 px-3 border border-slate-300 rounded-md text-sm" />
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">Optional. Use for remote/hardship postings.</div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per diem?</label>
            <div className="mt-1 flex items-center gap-2 h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.per_diem_applies}
                  onChange={(e) => set("per_diem_applies", e.target.checked)}
                  data-testid="prop-per_diem_applies" className="h-4 w-4 accent-teal-600" />
                Applies
              </label>
              {f.per_diem_applies && (
                <input type="number" step="0.01" value={f.per_diem_rate}
                  onChange={(e) => set("per_diem_rate", e.target.value)}
                  data-testid="prop-per_diem_rate" placeholder="$ / day"
                  className="flex-1 h-9 px-3 border border-slate-300 rounded-md text-sm" />
              )}
            </div>
          </div>
          {f.per_diem_applies && (
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per diem billed</label>
              <select value={f.per_diem_frequency} onChange={(e) => set("per_diem_frequency", e.target.value)}
                data-testid="prop-per_diem_frequency"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md">
                <option value="every_day_worked">Every day worked</option>
                <option value="5_per_week">5 days / week (regardless of days worked)</option>
                <option value="7_per_week">7 days / week (regardless of days worked)</option>
              </select>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Overtime</label>
            <div className="mt-1 flex flex-wrap items-center gap-3 min-h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.ot_rate_applies}
                  onChange={(e) => set("ot_rate_applies", e.target.checked)}
                  data-testid="prop-ot_rate_applies" className="h-4 w-4 accent-teal-600" />
                Bill overtime above a weekly threshold
              </label>
              {f.ot_rate_applies && (
                <>
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xs text-slate-500">Rate:</span>
                    <input type="number" step="0.01" value={f.ot_rate}
                      onChange={(e) => set("ot_rate", e.target.value)}
                      data-testid="prop-ot_rate" placeholder="$ / hr"
                      className="w-28 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <span className="text-xs text-slate-500">After:</span>
                    <input type="number" step="0.5" value={f.ot_threshold_hours}
                      onChange={(e) => set("ot_threshold_hours", e.target.value)}
                      data-testid="prop-ot_threshold_hours"
                      className="w-20 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                    <span className="text-xs text-slate-500">hrs / week</span>
                  </div>
                </>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">Standard is 40 hrs/week. Leave off if the client is straight-time only regardless of hours.</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday rate</label>
            <div className="mt-1 flex flex-wrap items-center gap-3 min-h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.holiday_rate_applies}
                  onChange={(e) => set("holiday_rate_applies", e.target.checked)}
                  data-testid="prop-holiday_rate_applies" className="h-4 w-4 accent-teal-600" />
                Bill a premium rate for work on designated holidays
              </label>
              {f.holiday_rate_applies && (
                <div className="inline-flex items-center gap-1">
                  <span className="text-xs text-slate-500">Rate:</span>
                  <input type="number" step="0.01" value={f.holiday_rate}
                    onChange={(e) => set("holiday_rate", e.target.value)}
                    data-testid="prop-holiday_rate" placeholder="$ / hr"
                    className="w-28 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                </div>
              )}
            </div>
            {f.holiday_rate_applies && (
              <div className="mt-2">
                <div className="text-xs font-semibold text-slate-600 mb-1">Designated holiday dates (shown to the client on the proposal)</div>
                <HolidayDatesEditor value={f.holiday_dates || []} onChange={(v) => set("holiday_dates", v)} idPrefix="prop-holiday" />
              </div>
            )}
            <div className="mt-1 text-xs text-slate-500">Holiday hours are billed in lieu of overtime, not on top of it. The dates carry onto the Project when the proposal is converted.</div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Mobilization / demob paid?</label>
            <div className="mt-1 flex items-center gap-3 h-11">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.mobilization_applies}
                  onChange={(e) => set("mobilization_applies", e.target.checked)}
                  data-testid="prop-mobilization_applies" className="h-4 w-4 accent-teal-600" />
                Client pays travel to/from site
              </label>
              {f.mobilization_applies && (
                <>
                  <span className="text-xs text-slate-500">Cap each way (hrs, optional):</span>
                  <input type="number" step="0.5" value={f.mobilization_hours_cap}
                    onChange={(e) => set("mobilization_hours_cap", e.target.value)}
                    data-testid="prop-mobilization_hours_cap" placeholder="e.g. 6"
                    className="w-24 h-9 px-3 border border-slate-300 rounded-md text-sm" />
                </>
              )}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Special terms &amp; conditions <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span></label>
            <textarea rows={3} value={f.special_terms} onChange={(e) => set("special_terms", e.target.value)}
              data-testid="prop-special_terms" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" placeholder="Anything unique to this job — e.g. shift schedule, PPE, drug-testing requirements" />
          </div>

          <div className="sm:col-span-2 mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md" data-testid="prop-auto-convert-box">
            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <input type="checkbox" checked={!!f.auto_convert}
                onChange={(e) => set("auto_convert", e.target.checked)}
                data-testid="prop-auto-convert" className="mt-0.5 h-4 w-4 accent-emerald-600" />
              <span className="text-emerald-900">
                <b>When the client accepts, create the Client + Project automatically</b> — no Convert step. You'll get an email with an <i>Assign workers</i> link.
              </span>
            </label>
            {f.auto_convert && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Project name <span className="font-normal normal-case tracking-normal">(optional)</span></label>
                  <input value={f.project_name} onChange={(e) => set("project_name", e.target.value)} data-testid="prop-project-name"
                    placeholder={`${f.company_name || "Client"} — ${f.location || "Project"}`} className="mt-1 w-full h-10 px-3 border border-emerald-300 rounded-md bg-white text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">PO number <span className="font-normal normal-case tracking-normal">(optional)</span></label>
                  <input value={f.po_number} onChange={(e) => set("po_number", e.target.value)} data-testid="prop-po-number"
                    placeholder="Add later if unknown" className="mt-1 w-full h-10 px-3 border border-emerald-300 rounded-md bg-white text-sm" />
                </div>
                {!isEdit && clientId === NEW_CLIENT && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">Client initials <span className="font-normal normal-case tracking-normal">(for invoice numbers)</span></label>
                    <input value={f.client_initials} maxLength={6} onChange={(e) => set("client_initials", e.target.value.toUpperCase())} data-testid="prop-client-initials"
                      placeholder="Auto from name" className="mt-1 w-32 h-10 px-3 border border-emerald-300 rounded-md bg-white text-sm font-mono uppercase" />
                  </div>
                )}
              </div>
            )}
          </div>

          {isEdit && !isSales && (
            <div className="sm:col-span-2 mt-3 p-3 bg-teal-50 border border-teal-200 rounded-md">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={resendOnSave}
                  onChange={(e) => setResendOnSave(e.target.checked)}
                  data-testid="prop-resend-on-save" className="mt-0.5 h-4 w-4 accent-teal-600" />
                <span className="text-slate-800">
                  {["pending_review", "changes_requested"].includes(initial?.status)
                    ? <><b>Approve &amp; send to the client after saving</b> — this proposal came from a sales rep and hasn't gone out yet. Uncheck to save your edits and keep it in the review queue.</>
                    : <><b>Resend to client after saving</b> — emails an updated PDF + fresh {f.validity_days}-day link. Uncheck for quiet edits (e.g. fixing a typo before the client opens the email).</>}
                </span>
              </label>
            </div>
          )}
          {isSales && (
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-slate-200 text-xs text-slate-500" data-testid="prop-sales-footnote">
              {needsReview
                ? <>Nothing goes to the client yet. {isEdit ? "Resubmitting puts it back in the admin review queue." : "An admin approves and sends it; the client then gets the branded email + PDF with a"} <b>{f.validity_days}-day</b> acceptance link.</>
                : <>This goes to <b>{f.contact_email || "the client"}</b> immediately with a <b>{f.validity_days}-day</b> acceptance link, and the office gets an FYI email. Double-check the rate before you send.</>}
            </div>
          )}
          {!isEdit && !isSales && (
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-slate-200 text-xs text-slate-500">
              The client will receive a branded email with a secure link + attached PDF. Their acceptance creates a signed PDF stored on this server; the link expires in <b>{f.validity_days} days</b>.
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
          <button type="button" onClick={onClose}
            className="h-11 px-4 border border-slate-300 rounded-md font-semibold hover:bg-slate-50">
            Cancel
          </button>
          <button data-testid="prop-send-btn" type="submit" disabled={busy}
            className="h-11 px-5 bg-teal-700 hover:bg-teal-800 text-white font-bold rounded-md disabled:opacity-50 flex items-center gap-2 justify-center">
            {isEdit ? <FloppyDisk size={18} weight="bold" /> : <FilePdf size={18} weight="bold" />}
            {busy
              ? (isSales ? (needsReview ? "Submitting…" : "Sending…") : isEdit ? "Saving…" : "Sending…")
              : isSales ? (needsReview ? (isEdit ? "Resubmit for review" : "Submit for review") : (isEdit ? "Send to client" : "Send proposal"))
              : (isEdit ? (resendOnSave ? "Save & Resend" : "Save changes") : "Send proposal")}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
